import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import {
  FlaskConical, Zap, RotateCcw, Activity, ChevronUp, ChevronDown,
  Loader2, Camera, Copy, Link as LinkIcon, Eraser, Video, Square, Sparkles,
  Volume2, Download, BookMarked, Save, Trash2, Play, Share2, Mail,
  Wand2, Circle, Clapperboard, HelpCircle, X,
} from 'lucide-react';
import html2canvas from 'html2canvas';
import { toast } from 'sonner';
import api from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { formatClock, msToSrt, describeTarget, downloadTextFile, blobToBase64 } from './demoToolkitUtils';
import DemoAudioPlayer from './DemoAudioPlayer';
import DemoRecordingOverlay from './DemoRecordingOverlay';
import DemoDirector from './DemoDirector';
import DemoSeedTab from './tabs/DemoSeedTab';
import DemoCaptureTab from './tabs/DemoCaptureTab';
import DemoPresetsList from './DemoPresetsList';
import DemoWarmupButton from './DemoWarmupButton';
import { DEMO_TOOLKIT_TOUR_STEPS, TOUR_SEEN_KEY, TOUR_PULSE_CLASS } from './demoToolkitTourSteps';
import { usePulseHighlight, useLocalStorageFlag } from '../../hooks';

const DEMO_ORG_ID = 'org_acme_demo';

/**
 * Floating recording-helper dock for the Acme demo tenant.
 * Shown only when a super-admin is on Acme (either logged-in directly or
 * previewing via the org switcher). Always-visible no matter which page
 * the user is on, so videos can trigger demo moments without navigating.
 */
export const DemoToolkit = () => {
  const { user, isSuperAdmin, viewAsOrg } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState(null);

  // Recording state
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const startedAtRef = useRef(0);
  const captionsRef = useRef([]);        // [{ t, testid, tag, text, path }]
  const recordingRef = useRef(false);    // mirror for event listener
  const [lastRecording, setLastRecording] = useState(null); // {events, total_ms} kept after stop
  const [lastScript, setLastScript] = useState('');         // markdown text from GPT-5.2
  const [lastAudioUrl, setLastAudioUrl] = useState('');     // blob URL for TTS MP3
  const [ttsVoice, setTtsVoice] = useState('onyx');         // onyx|nova|alloy|shimmer|echo|fable|sage|coral|ash
  const audioRef = useRef(null);

  // Preset library state
  const [presetsOpen, setPresetsOpen] = useState(false);
  const [presets, setPresets] = useState([]);
  const [presetsLoading, setPresetsLoading] = useState(false);
  const [saveFormOpen, setSaveFormOpen] = useState(false);
  const [saveForm, setSaveForm] = useState({ scenario: '', description: '', tags: '' });
  const lastAudioBlobRef = useRef(null); // keep the raw Blob so we can re-upload it

  // Demo Story Director modal
  const [directorOpen, setDirectorOpen] = useState(false);
  // When the digest email's "Draft follow-ups for all N →" link is
  // clicked, the URL carries `?demo_insights=batch_reengage&template_ids=tid1,tid2`.
  // We auto-open the Director, jump to the Insights tab, and seed
  // the Batch Re-engage view with those template ids. Read once on
  // location change.
  const [pendingBatchReengageIds, setPendingBatchReengageIds] = useState(null);

  // Ref keeps the `rehearseDemo` callback current for the keyboard
  // listener below (which is attached once but needs to see the
  // latest state — busy flag, captured closures, etc).
  const rehearseRef = useRef(null);

  // Active tab for the consolidated Session/Capture/Seed/Narration/Actions
  // layout. When a recording starts we force-focus Session so the
  // "Stop session" button is always one click away.
  const [activeTab, setActiveTab] = useState('session');
  useEffect(() => {
    if (recording) setActiveTab('session');
  }, [recording]);

  /* ────── One-time "⚡ Tour" ──────
   * First time a super-admin opens the toolkit on Acme, walk them
   * through the 5 tabs in 30 seconds. State lives in localStorage
   * via `useLocalStorageFlag` (private-mode / Safari-ITP safe —
   * hook falls back to pure React state silently).
   *
   * `tourStep === null` → not running. 0..N-1 → currently showing step N.
   * Skip-or-finish both flip the `tourSeen` flag so repeat opens
   * never reshow. Copy + per-step target testids live in
   * `/app/frontend/src/components/demo/demoToolkitTourSteps.js`.
   */
  const TOUR_STEPS = DEMO_TOOLKIT_TOUR_STEPS;
  const [tourSeen, setTourSeen, resetTourSeen] = useLocalStorageFlag(TOUR_SEEN_KEY, false);
  const [tourStep, setTourStep] = useState(null);

  // Fire-and-forget telemetry — never awaited, never throws. A flaky
  // tracking endpoint must not break the onboarding experience.
  const _trackTour = useCallback((event, step) => {
    try {
      api.post('/analytics/tour/track', {
        event,
        step: typeof step === 'number' ? step : 0,
        tour_id: 'demo_toolkit_v1',
        page_path: typeof window !== 'undefined' ? window.location.pathname : null,
      }).catch(() => {});
    } catch {
      /* noop */
    }
  }, []);

  // Fire-and-forget toolkit-action telemetry. Lets PMs answer "which
  // toolkit features get used in real demos, and which sit idle?".
  // Action names mirror the `ACTIONS` set in `/app/backend/routes/
  // toolkit_analytics.py` — if you add a new one, add it there too
  // or the backend will 422 it.
  const _trackAction = useCallback((action, context = 'click') => {
    try {
      api.post('/analytics/toolkit/track', {
        action,
        context,
        page_path: typeof window !== 'undefined' ? window.location.pathname : null,
      }).catch(() => {});
    } catch {
      /* noop */
    }
  }, []);

  useEffect(() => {
    if (expanded && tourStep === null && !tourSeen) {
      setTourStep(0);
      // telemetry effect below will auto-fire 'open' on the null→0 transition
    }
  }, [expanded, tourStep, tourSeen]);

  // External trigger from the centered ToolkitFAB (Feb 28 2026).
  // The right-side toggle button at the bottom of this file is now
  // hidden, so the only entry point is via this event.
  useEffect(() => {
    const onToggle = () => setExpanded((v) => !v);
    const onOpen = () => setExpanded(true);
    window.addEventListener('lie:toggleDemoToolkit', onToggle);
    window.addEventListener('lie:openDemoToolkit', onOpen);
    return () => {
      window.removeEventListener('lie:toggleDemoToolkit', onToggle);
      window.removeEventListener('lie:openDemoToolkit', onOpen);
    };
  }, []);

  const dismissTour = useCallback(() => {
    // Fires a 'prev non-null → tourStep === null' transition which the
    // telemetry effect below classifies as a 'skip' (or 'finish' if
    // it came from the last step).
    setTourStep(null);
    setTourSeen(true);
  }, [setTourSeen]);

  const advanceTour = useCallback(() => {
    setTourStep((s) => {
      if (s === null) return null;
      const next = s + 1;
      if (next >= TOUR_STEPS.length) {
        return null;  // telemetry effect detects finish (prev === last step)
      }
      setActiveTab(TOUR_STEPS[next].tab);
      return next;
    });
  }, [TOUR_STEPS]);

  const rewindTour = useCallback(() => {
    setTourStep((s) => {
      if (s === null || s === 0) return s;
      const prev = s - 1;
      setActiveTab(TOUR_STEPS[prev].tab);
      return prev;
    });
  }, [TOUR_STEPS]);

  // Exposed for the "Replay tour" button in the toolkit header —
  // clears the storage flag and re-enters step 0.
  const restartTour = useCallback(() => {
    resetTourSeen();
    setActiveTab('session');
    setTourStep(0);
  }, [resetTourSeen]);

  // Single source of truth for tour telemetry: observe the committed
  // state rather than firing inside setState updaters (which run twice
  // under React StrictMode, double-counting events). Every transition
  // between tour steps maps to exactly one event:
  //   null → 0              → 'open'
  //   N    → N+1   (N>=0)   → 'advance'
  //   N    → N-1            → 'rewind'
  //   last → null           → 'finish' (advanceTour past the end)
  //   N    → null   (N<last)→ 'skip'  (X button before the end)
  const prevStepRef = useRef(null);
  useEffect(() => {
    const prev = prevStepRef.current;
    prevStepRef.current = tourStep;

    if (prev === tourStep) return;  // no-op (shouldn't fire but be safe)

    if (prev === null && tourStep !== null) {
      _trackTour('open', tourStep);
    } else if (prev !== null && tourStep !== null) {
      _trackTour(tourStep > prev ? 'advance' : 'rewind', tourStep);
    } else if (prev !== null && tourStep === null) {
      const reachedEnd = prev === TOUR_STEPS.length - 1;
      _trackTour(reachedEnd ? 'finish' : 'skip', prev);
      if (reachedEnd) setTourSeen(true);
    }
  }, [tourStep, TOUR_STEPS, _trackTour, setTourSeen]);

  // Pulse the `testids_to_highlight` on the active step so the SE can
  // see exactly which button the copy is pointing at. Uses the shared
  // `usePulseHighlight` hook — same primitive powering GlossaryMode
  // and any future "look here" overlays.
  const activeStepTestids = tourStep !== null
    ? (TOUR_STEPS[tourStep]?.testids_to_highlight || [])
    : [];
  usePulseHighlight({
    selectors: activeStepTestids.map((tid) => `[data-testid="${tid}"]`),
    active: tourStep !== null && activeStepTestids.length > 0,
    className: TOUR_PULSE_CLASS,
  });

  const effectiveOrgId = viewAsOrg?.id || user?.organization_id;
  const isAcme = effectiveOrgId === DEMO_ORG_ID;

  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    const intent = params.get('demo_insights');
    const ids = (params.get('template_ids') || '').split(',').map((s) => s.trim()).filter(Boolean);
    if (intent === 'batch_reengage' && ids.length > 0 && isAcme) {
      setPendingBatchReengageIds(ids);
      setDirectorOpen(true);
    } else if (intent === 'open' && isAcme) {
      setDirectorOpen(true);
    }
    // Don't strip the query yet — the modal needs it during mount.
    // Cleared by `clearBatchReengageIntent` below once consumed.
  }, [location.search, isAcme]);

  // Once the Director consumes the pending ids, drop them from local
  // state AND from the URL so a refresh doesn't re-trigger.
  const clearBatchReengageIntent = React.useCallback(() => {
    setPendingBatchReengageIds(null);
    const next = new URLSearchParams(location.search || '');
    next.delete('demo_insights');
    next.delete('template_ids');
    const qs = next.toString();
    window.history.replaceState({}, '', `${location.pathname}${qs ? `?${qs}` : ''}`);
  }, [location.pathname, location.search]);


  /* ---- Tick timer while recording ---- */
  useEffect(() => {
    if (!recording) return undefined;
    const id = setInterval(() => {
      setElapsed(Date.now() - startedAtRef.current);
    }, 500);
    return () => clearInterval(id);
  }, [recording]);

  /* ---- Route changes are captured as caption events ---- */
  useEffect(() => {
    if (!recordingRef.current) return;
    captionsRef.current.push({
      t: Date.now() - startedAtRef.current,
      kind: 'route',
      path: location.pathname + (location.search || ''),
      text: `Navigated to ${location.pathname}`,
    });
  }, [location]);

  /* ---- Global click listener while recording ---- */
  useEffect(() => {
    if (!recording) return undefined;
    const handler = (ev) => {
      // Ignore interactions with the toolkit itself
      const toolkit = document.querySelector('[data-testid="demo-toolkit"]');
      const overlay = document.querySelector('[data-testid="demo-recorder-overlay"]');
      if (toolkit?.contains(ev.target) || overlay?.contains(ev.target)) return;
      const info = describeTarget(ev.target);
      if (!info) return;
      captionsRef.current.push({
        t: Date.now() - startedAtRef.current,
        kind: 'click',
        path: window.location.pathname,
        ...info,
      });
    };
    document.addEventListener('click', handler, true);
    return () => document.removeEventListener('click', handler, true);
  }, [recording]);

  /* ---- Global Rehearse-demo hotkey ----
   * ⌘⇧R (mac) / Ctrl+Shift+R (win/linux) + custom `rehearse:fire`
   * DOM event → fires the same flow as the toolbar button. Gated on
   * super-admin-on-Acme (the only role that can call the backend
   * endpoints anyway — non-super attempts return 403).
   *
   * ⚠️ `Ctrl+Shift+R` IS the browser's hard-reload shortcut in
   * Chrome/Firefox/Safari. `preventDefault()` suppresses the reload
   * when focus is inside the app, but some browser chrome contexts
   * (e.g. address bar focus) still consume the key first. If that
   * becomes flaky, flip to `e.altKey` (Alt+Shift+R) — zero conflict
   * and one-line change.
   */
  useEffect(() => {
    if (!isSuperAdmin || !isAcme) return undefined;
    const onKey = (e) => {
      // Don't hijack while the user is typing somewhere.
      const tag = (e.target?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select' || e.target?.isContentEditable) return;
      const isMac = typeof navigator !== 'undefined' && /mac/i.test(navigator.platform || '');
      const primary = isMac ? e.metaKey : e.ctrlKey;
      if (primary && e.shiftKey && (e.key === 'R' || e.key === 'r')) {
        e.preventDefault();
        _trackAction('rehearse', 'hotkey');
        rehearseRef.current?.();
      }
    };
    const onCustomFire = () => rehearseRef.current?.();
    window.addEventListener('keydown', onKey);
    window.addEventListener('rehearse:fire', onCustomFire);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('rehearse:fire', onCustomFire);
    };
  }, [isSuperAdmin, isAcme]);

  if (!isSuperAdmin || !isAcme) return null;

  /* ---- existing actions ---- */

  const triggerAnomaly = async () => {
    _trackAction('trigger-anomaly');
    setBusy('trigger');
    try {
      const res = await api.post('/data-management/anomalies/demo/trigger');
      toast.success(res.data.headline, { description: 'Live demo anomaly injected.' });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to trigger anomaly');
    } finally {
      setBusy(null);
    }
  };

  const clearLiveAnomalies = async () => {
    _trackAction('clear-anomalies');
    setBusy('clear-anom');
    try {
      const res = await api.post('/data-management/anomalies/demo/clear');
      toast.success(`Cleared ${res.data.cleared} live demo anomal${res.data.cleared === 1 ? 'y' : 'ies'}`, {
        description: 'Seeded narrative anomalies preserved.',
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Clear failed');
    } finally {
      setBusy(null);
    }
  };

  const resetAcme = async () => {
    if (!window.confirm('Wipe and re-seed the Acme Industries demo tenant?\n\nThis returns all data to its pristine seeded state.')) return;
    _trackAction('reset-only');
    setBusy('reset');
    try {
      const res = await api.post('/admin/data-health/reset-acme-demo?apply=true');
      toast.success(`Acme reset: wiped ${res.data.wiped}, re-seeded ${res.data.inserted}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Reset failed');
    } finally {
      setBusy(null);
    }
  };

  /**
   * Rehearse: one-click "I need to run a demo in 10 seconds" helper.
   * Wipes + re-seeds the Acme tenant AND mints a fresh single-use
   * magic-link, then opens that link in a new tab. The new tab lands
   * straight on the Acme home dashboard as the demo admin — no copy-
   * paste, no login dance.
   *
   * Distinct from `startRecordingSession` (which *also* resets + mints
   * a link but copies it to clipboard and starts a caption recorder
   * for producing narration). Rehearsal is the lightweight "dry-run
   * before the real call" path.
   */
  const rehearseDemo = async () => {
    if (!window.confirm('Rehearse demo?\n\nThis will:\n1. Wipe & re-seed the Acme tenant (pristine state)\n2. Mint a single-use magic-link\n3. Open it in a new tab — logged in as the demo admin')) return;
    setBusy('rehearse');
    try {
      const reset = await api.post('/admin/data-health/reset-acme-demo?apply=true');
      const magic = await api.post('/data-management/demo/magic-link');

      let url = magic.data.url || '';
      if (url.startsWith('/')) url = window.location.origin + url;

      // Open *before* the toast so the browser's popup-blocker doesn't
      // flag the call as stemming from an async tool-tip rather than a
      // user gesture. window.open from an explicit button click is
      // allowed; from an awaited tool-tip it sometimes is not.
      const opened = window.open(url, '_blank', 'noopener,noreferrer');
      if (!opened) {
        // Fallback: clipboard copy so the SE can paste it manually if
        // the browser blocked the auto-open.
        try { await navigator.clipboard.writeText(url); } catch (_) { /* clipboard may be blocked */ }
        toast.warning('Popup blocked — link copied to clipboard', {
          description: `Paste it into a new tab · expires in ${magic.data.ttl_hours || 24}h`,
        });
      } else {
        toast.success('Rehearsal ready', {
          description: `Acme reseeded (${reset.data.inserted} rows) · fresh tab logged in as ${magic.data.demo_email}`,
        });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Rehearse failed');
    } finally {
      setBusy(null);
    }
  };

  // Keep the ref current so the hotkey listener (attached above)
  // always calls the latest version of the closure.
  rehearseRef.current = rehearseDemo;

  const capturePng = async () => {
    _trackAction('capture-png');
    setBusy('screenshot');
    try {
      setExpanded(false);
      const node = document.querySelector('main') || document.body;
      const toolkitEl = document.querySelector('[data-testid="demo-toolkit"]');
      const overlayEl = document.querySelector('[data-testid="demo-recorder-overlay"]');
      if (toolkitEl) toolkitEl.style.visibility = 'hidden';
      if (overlayEl) overlayEl.style.visibility = 'hidden';
      await new Promise((r) => setTimeout(r, 200));

      const canvas = await html2canvas(node, {
        backgroundColor: '#f8fafc',
        scale: 1.5,
        useCORS: true,
        logging: false,
        windowWidth: node.scrollWidth,
      });
      if (toolkitEl) toolkitEl.style.visibility = '';
      if (overlayEl) overlayEl.style.visibility = '';

      canvas.toBlob((blob) => {
        if (!blob) {
          toast.error('Screenshot capture failed');
          return;
        }
        const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 16);
        const title = (document.title || 'acme-snapshot').replace(/[^a-z0-9-_.]/gi, '_').slice(0, 50);
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `acme-${title}-${stamp}.png`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast.success('Screenshot downloaded');
      }, 'image/png');
    } catch (err) {
      toast.error('Screenshot failed: ' + (err?.message || 'unknown error'));
    } finally {
      setBusy(null);
    }
  };

  const copyJson = async () => {
    _trackAction('copy-json');
    setBusy('json');
    try {
      const res = await api.get(`/data-management/demo/snapshot?organization_id=${DEMO_ORG_ID}`);
      const text = JSON.stringify(res.data, null, 2);
      await navigator.clipboard.writeText(text);
      toast.success('Metrics snapshot copied to clipboard', {
        description: `${res.data.organization?.name} · ${res.data.period} · PE ${((res.data.people_engine?.pe_composite || 0) * 100).toFixed(0)}% · P${res.data.benchmark?.overall_percentile?.toFixed(0) || '–'}`,
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Snapshot failed');
    } finally {
      setBusy(null);
    }
  };

  const copyMagicLink = async () => {
    _trackAction('copy-demo-link');
    setBusy('magic');
    try {
      const res = await api.post('/data-management/demo/magic-link');
      let url = res.data.url || '';
      if (url.startsWith('/')) url = window.location.origin + url;
      await navigator.clipboard.writeText(url);
      const hours = res.data.ttl_hours || 24;
      toast.success('Shareable demo link copied', {
        description: `Single-use · expires in ${hours}h · auto-logs in as ${res.data.demo_email}`,
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not create demo link');
    } finally {
      setBusy(null);
    }
  };

  /* ---- Record-session flow ---- */

  const startRecordingSession = async () => {
    _trackAction('record-start');
    const ok = window.confirm(
      'Start a new demo recording session?\n\n' +
      '1. Acme Industries tenant will be wiped & re-seeded (pristine state)\n' +
      '2. A single-use magic link will be copied to your clipboard\n' +
      '3. A floating timer will appear — every click is auto-captioned\n' +
      '4. When you stop the session, the captions download as .srt + .json'
    );
    if (!ok) return;

    setBusy('record-start');
    setExpanded(false);
    try {
      // 1) Reset Acme
      const reset = await api.post('/admin/data-health/reset-acme-demo?apply=true');

      // 2) Magic link
      const magic = await api.post('/data-management/demo/magic-link');
      let url = magic.data.url || '';
      if (url.startsWith('/')) url = window.location.origin + url;
      try { await navigator.clipboard.writeText(url); } catch (_) { /* clipboard may be blocked */ }

      // 3) Start recorder
      const now = Date.now();
      startedAtRef.current = now;
      captionsRef.current = [
        { t: 0, kind: 'system', text: `Demo session started — Acme wiped (${reset.data.wiped}) + re-seeded (${reset.data.inserted})` },
        { t: 0, kind: 'system', text: `Magic link copied: ${url}` },
        { t: 0, kind: 'route', path: location.pathname, text: `Starting on ${location.pathname}` },
      ];
      recordingRef.current = true;
      setElapsed(0);
      setRecording(true);

      toast.success('Recording started', {
        description: 'Magic link copied to clipboard · every click is now captioned',
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not start recording session');
    } finally {
      setBusy(null);
    }
  };

  const stopRecordingSession = () => {
    _trackAction('record-stop');
    if (!recordingRef.current) return;
    const totalMs = Date.now() - startedAtRef.current;
    const events = [...captionsRef.current];

    recordingRef.current = false;
    setRecording(false);
    setElapsed(0);

    if (events.length === 0) {
      toast.info('Recording stopped — no events captured');
      return;
    }

    // Build SRT: each caption runs for 3s (or until next)
    const srt = events.map((e, i) => {
      const start = e.t;
      const end = (i + 1 < events.length ? events[i + 1].t : start + 3000);
      const safeEnd = Math.max(end, start + 800);
      const label = e.kind === 'click'
        ? `CLICK${e.testid ? ` [${e.testid}]` : ''}${e.text ? ` — ${e.text}` : ''}`
        : e.kind === 'route'
          ? `ROUTE → ${e.path}`
          : e.text;
      return `${i + 1}\n${msToSrt(start)} --> ${msToSrt(safeEnd)}\n${label}\n`;
    }).join('\n');

    const json = JSON.stringify({
      recorded_at: new Date().toISOString(),
      total_ms: totalMs,
      event_count: events.length,
      events,
    }, null, 2);

    const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 16);
    downloadTextFile(`acme-demo-session-${stamp}.srt`, srt, 'application/x-subrip');
    downloadTextFile(`acme-demo-session-${stamp}.json`, json, 'application/json');

    captionsRef.current = [];
    setLastRecording({ events, total_ms: totalMs });
    // Clear any previously-generated narration/audio — new recording supersedes it
    setLastScript('');
    if (lastAudioUrl) { try { URL.revokeObjectURL(lastAudioUrl); } catch (_) { /* noop */ } }
    setLastAudioUrl('');
    toast.success(`Recording stopped — ${events.length} events captured`, {
      description: `Downloaded .srt + .json · total ${formatClock(totalMs)} · script generator now available`,
    });
  };

  const generateNarration = async () => {
    _trackAction('generate-narration');
    if (!lastRecording) return;
    setBusy('narrate');
    try {
      const res = await api.post('/data-management/demo/generate-narration', {
        events: lastRecording.events,
        total_ms: lastRecording.total_ms,
      });
      const script = res.data?.script || '';
      if (!script) {
        toast.error('LLM returned an empty script');
        return;
      }
      setLastScript(script);
      // New script invalidates any prior audio
      if (lastAudioUrl) { try { URL.revokeObjectURL(lastAudioUrl); } catch (_) { /* noop */ } }
      setLastAudioUrl('');
      const header =
        `# Acme Industries Demo — Voice-Over Script\n\n` +
        `_${res.data.event_count} events · ${res.data.total_clock} · model ${res.data.model}_\n\n---\n\n`;
      const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 16);
      downloadTextFile(
        `acme-demo-script-${stamp}.md`,
        header + script,
        'text/markdown;charset=utf-8',
      );
      // Also copy to clipboard for quick paste into Loom/Riverside teleprompter
      try { await navigator.clipboard.writeText(script); } catch (_) { /* clipboard may be blocked */ }
      toast.success('Voice-over script ready', {
        description: `Downloaded .md + copied to clipboard · ${res.data.model}`,
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Script generation failed');
    } finally {
      setBusy(null);
    }
  };

  const playScriptAloud = async () => {
    _trackAction('play-aloud');
    if (!lastScript) return;
    setBusy('tts');
    try {
      // axios default returns JSON — request as blob explicitly
      const res = await api.post(
        '/data-management/demo/narration-audio',
        { script: lastScript, voice: ttsVoice, model: 'tts-1-hd' },
        { responseType: 'blob' },
      );
      // Revoke previous
      if (lastAudioUrl) { try { URL.revokeObjectURL(lastAudioUrl); } catch (_) { /* noop */ } }
      const blob = new Blob([res.data], { type: 'audio/mpeg' });
      lastAudioBlobRef.current = blob;
      const url = URL.createObjectURL(blob);
      setLastAudioUrl(url);
      toast.success('Narration generated', {
        description: `Voice: ${ttsVoice} · tts-1-hd · ${(blob.size / 1024).toFixed(0)} KB`,
      });
      // Auto-play after the player mounts
      setTimeout(() => {
        if (audioRef.current) {
          audioRef.current.load();
          audioRef.current.play().catch(() => { /* autoplay may be blocked */ });
        }
      }, 150);
    } catch (err) {
      // Blob responses can't JSON-parse error — fall back to text
      let detail = 'TTS failed';
      try {
        if (err?.response?.data instanceof Blob) {
          const txt = await err.response.data.text();
          try { detail = JSON.parse(txt).detail || detail; } catch { detail = txt || detail; }
        } else {
          detail = err?.response?.data?.detail || err?.message || detail;
        }
      } catch { /* noop */ }
      toast.error(detail);
    } finally {
      setBusy(null);
    }
  };

  const downloadLastAudio = () => {
    if (!lastAudioUrl) return;
    const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 16);
    const a = document.createElement('a');
    a.href = lastAudioUrl;
    a.download = `acme-demo-narration-${ttsVoice}-${stamp}.mp3`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  /* ---- Preset library ---- */

  const loadPresets = async () => {
    setPresetsLoading(true);
    try {
      const res = await api.get('/data-management/demo/clips');
      setPresets(res.data?.clips || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load presets');
    } finally {
      setPresetsLoading(false);
    }
  };

  const togglePresets = () => {
    const next = !presetsOpen;
    setPresetsOpen(next);
    if (next) loadPresets();
  };

  const savePreset = async () => {
    _trackAction('save-preset');
    if (!lastScript || !lastAudioBlobRef.current || !lastRecording) {
      toast.error('Record + generate script + play aloud first, then save as preset.');
      return;
    }
    const scenario = (saveForm.scenario || '').trim();
    if (!scenario) {
      toast.error('Give the preset a scenario name first');
      return;
    }
    setBusy('save-preset');
    try {
      const audio_base64 = await blobToBase64(lastAudioBlobRef.current);
      const tags = saveForm.tags
        ? saveForm.tags.split(',').map(t => t.trim()).filter(Boolean)
        : [];
      const res = await api.post('/data-management/demo/clips', {
        scenario,
        description: saveForm.description || '',
        tags,
        script: lastScript,
        voice: ttsVoice,
        events: lastRecording.events,
        total_ms: lastRecording.total_ms,
        audio_base64,
      });
      toast.success('Preset saved', {
        description: `${res.data.scenario} · ${(res.data.audio_size_bytes / 1024).toFixed(0)} KB`,
      });
      setSaveForm({ scenario: '', description: '', tags: '' });
      setSaveFormOpen(false);
      if (presetsOpen) loadPresets();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save preset');
    } finally {
      setBusy(null);
    }
  };

  const playPreset = async (clip) => {
    _trackAction('preset-play');
    setBusy(`play-${clip.clip_id}`);
    try {
      const [metaRes, audioRes] = await Promise.all([
        api.get(`/data-management/demo/clips/${clip.clip_id}`),
        api.get(`/data-management/demo/clips/${clip.clip_id}/audio`, { responseType: 'blob' }),
      ]);
      const meta = metaRes.data || {};
      setLastScript(meta.script || '');
      setTtsVoice(meta.voice || 'onyx');
      setLastRecording({ events: meta.events || [], total_ms: meta.total_ms || 0 });
      if (lastAudioUrl) { try { URL.revokeObjectURL(lastAudioUrl); } catch (_) { /* noop */ } }
      const blob = new Blob([audioRes.data], { type: 'audio/mpeg' });
      lastAudioBlobRef.current = blob;
      const url = URL.createObjectURL(blob);
      setLastAudioUrl(url);
      setTimeout(() => {
        if (audioRef.current) {
          audioRef.current.load();
          audioRef.current.play().catch(() => { /* autoplay blocked */ });
        }
      }, 150);
      toast.success(`Loaded preset: ${clip.scenario}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not load preset');
    } finally {
      setBusy(null);
    }
  };

  const deletePreset = async (clip) => {
    _trackAction('preset-delete');
    if (!window.confirm(`Delete preset "${clip.scenario}"?`)) return;
    setBusy(`del-${clip.clip_id}`);
    try {
      await api.delete(`/data-management/demo/clips/${clip.clip_id}`);
      setPresets((prev) => prev.filter((p) => p.clip_id !== clip.clip_id));
      toast.success('Preset deleted');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Delete failed');
    } finally {
      setBusy(null);
    }
  };

  const copyPublicLink = async (clip) => {
    _trackAction('preset-share');
    setBusy(`share-${clip.clip_id}`);
    try {
      const res = await api.post(`/data-management/demo/clips/${clip.clip_id}/public-preview`);
      let url = res.data?.url || '';
      if (url.startsWith('/')) url = window.location.origin + url;
      await navigator.clipboard.writeText(url);
      const hours = res.data?.ttl_hours || 168;
      const days = Math.round(hours / 24);
      toast.success('Public preview link copied', {
        description: `Valid for ${days} day${days === 1 ? '' : 's'} · no login required · ${clip.scenario}`,
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not create public link');
    } finally {
      setBusy(null);
    }
  };

  const sendDigestNow = async () => {
    _trackAction('send-digest');
    if (!window.confirm(
      'Send the Demo Director Digest email now?\n\n' +
      'All active super-admins will receive a summary of the last 7 days of ' +
      'preset views, completions, hot prospects, stale presets, and CTA clicks.'
    )) return;
    setBusy('digest');
    try {
      const res = await api.post('/data-management/demo/director-digest/send-now');
      if (res.data?.skipped_reason) {
        toast.info('Digest not sent', {
          description: `Reason: ${res.data.skipped_reason.replaceAll('_', ' ')}`,
        });
      } else {
        const t = res.data?.totals || {};
        toast.success(`Digest emailed to ${res.data.sent}/${res.data.recipients} super-admins`, {
          description: `${t.views || 0} views · ${t.cta_clicks || 0} CTAs · ${res.data.hot_count || 0} hot prospects`,
        });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Digest send failed');
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      {/* ---- Floating Audio Player (top-right, only when no active recording) ---- */}
      {!recording && (
        <DemoAudioPlayer
          audioRef={audioRef}
          lastAudioUrl={lastAudioUrl}
          ttsVoice={ttsVoice}
          onDownload={downloadLastAudio}
          onClose={() => {
            if (audioRef.current) audioRef.current.pause();
            if (lastAudioUrl) { try { URL.revokeObjectURL(lastAudioUrl); } catch (_) { /* noop */ } }
            setLastAudioUrl('');
          }}
        />
      )}

      {/* ---- Floating Recording Overlay (top-right) ---- */}
      <DemoRecordingOverlay
        recording={recording}
        elapsed={elapsed}
        eventCount={captionsRef.current.length}
        onStop={stopRecordingSession}
      />

      {/* ---- Floating Toolkit Dock ---- */}
      {/* Centered-bottom (was right-6) so the expanded panel no longer
          shadows wizard Next/Back controls anchored on the right edge. */}
      <div
        className="fixed bottom-20 left-1/2 -translate-x-1/2 z-50 flex flex-col items-center"
        data-testid="demo-toolkit"
      >
        {expanded && (
          <div
            className="mb-2 flex flex-col rounded-xl border border-slate-200 bg-white shadow-xl w-[360px] max-h-[560px] overflow-hidden"
            data-testid="demo-toolkit-panel"
          >
            {/* Header — fixed, never scrolls */}
            <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50/60 px-3 py-2">
              <div className="flex items-center gap-2">
                <FlaskConical className="h-4 w-4 text-indigo-600" />
                <span className="text-xs font-semibold tracking-tight text-slate-900">Demo Toolkit</span>
              </div>
              <div className="flex items-center gap-1.5">
                {recording && (
                  <span className="flex items-center gap-1 rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-semibold text-rose-700">
                    <Circle className="h-2 w-2 fill-rose-600 text-rose-600 animate-pulse" />
                    REC {formatClock(elapsed)}
                  </span>
                )}
                <button
                  type="button"
                  onClick={restartTour}
                  disabled={tourStep !== null}
                  title="Replay the 30-second tour"
                  className="flex h-6 w-6 items-center justify-center rounded-full text-slate-400 transition-colors hover:bg-slate-200 hover:text-slate-700 disabled:opacity-40"
                  data-testid="toolkit-replay-tour-btn"
                >
                  <HelpCircle className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>

            {/* ⚡ One-time tour overlay — covers the tab content area only,
             * so the tab strip stays visible and highlighted as the SE
             * watches the active tab change step-by-step. */}
            {tourStep !== null && TOUR_STEPS[tourStep] && (
              <div
                className="relative flex flex-col gap-2 border-b border-amber-200 bg-gradient-to-br from-amber-50 to-white px-3 py-3"
                data-testid="toolkit-tour-overlay"
              >
                <div className="flex items-start gap-2">
                  <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
                  <div className="flex-1">
                    <div className="text-[11px] font-semibold uppercase tracking-wider text-amber-700" data-testid={`toolkit-tour-step-${tourStep}`}>
                      {TOUR_STEPS[tourStep].title}
                    </div>
                    <p className="mt-0.5 text-[11.5px] leading-snug text-slate-700">
                      {TOUR_STEPS[tourStep].body}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={dismissTour}
                    title="Skip tour"
                    className="shrink-0 rounded-md p-1 text-slate-400 transition-colors hover:bg-slate-200 hover:text-slate-700"
                    data-testid="toolkit-tour-skip-btn"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
                <div className="flex items-center justify-between">
                  <button
                    type="button"
                    onClick={rewindTour}
                    disabled={tourStep === 0}
                    className="rounded-md px-2 py-1 text-[11px] font-medium text-slate-600 transition-colors hover:bg-slate-100 disabled:opacity-40"
                    data-testid="toolkit-tour-prev-btn"
                  >
                    ← Back
                  </button>
                  <div className="flex items-center gap-1">
                    {TOUR_STEPS.map((_, i) => (
                      <span
                        key={i}
                        className={`h-1.5 w-1.5 rounded-full transition-colors ${
                          i === tourStep ? 'bg-amber-500' : i < tourStep ? 'bg-amber-200' : 'bg-slate-200'
                        }`}
                      />
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={advanceTour}
                    className="rounded-md bg-amber-500 px-3 py-1 text-[11px] font-semibold text-white transition-colors hover:bg-amber-600"
                    data-testid={tourStep === TOUR_STEPS.length - 1 ? 'toolkit-tour-finish-btn' : 'toolkit-tour-next-btn'}
                  >
                    {tourStep === TOUR_STEPS.length - 1 ? 'Got it ✓' : 'Next →'}
                  </button>
                </div>
              </div>
            )}

            <Tabs value={activeTab} onValueChange={setActiveTab} className="flex min-h-0 flex-1 flex-col">
              <TabsList
                className="grid w-full grid-cols-5 gap-0.5 rounded-none border-b border-slate-100 bg-white px-2 py-1.5 h-auto"
                data-testid="toolkit-tabs-list"
              >
                <TabsTrigger value="session" className="text-[10.5px] font-semibold px-1 py-1 rounded-md data-[state=active]:bg-slate-100 data-[state=active]:text-slate-900 data-[state=active]:shadow-none text-slate-500" data-testid="toolkit-tab-session">Session</TabsTrigger>
                <TabsTrigger value="capture" disabled={recording} className="text-[10.5px] font-semibold px-1 py-1 rounded-md data-[state=active]:bg-slate-100 data-[state=active]:text-slate-900 data-[state=active]:shadow-none text-slate-500 disabled:opacity-40" data-testid="toolkit-tab-capture">Capture</TabsTrigger>
                <TabsTrigger value="seed" disabled={recording} className="text-[10.5px] font-semibold px-1 py-1 rounded-md data-[state=active]:bg-slate-100 data-[state=active]:text-slate-900 data-[state=active]:shadow-none text-slate-500 disabled:opacity-40" data-testid="toolkit-tab-seed">Seed</TabsTrigger>
                <TabsTrigger value="narration" disabled={recording} className="text-[10.5px] font-semibold px-1 py-1 rounded-md data-[state=active]:bg-slate-100 data-[state=active]:text-slate-900 data-[state=active]:shadow-none text-slate-500 disabled:opacity-40" data-testid="toolkit-tab-narration">Narration</TabsTrigger>
                <TabsTrigger value="actions" disabled={recording} className="text-[10.5px] font-semibold px-1 py-1 rounded-md data-[state=active]:bg-slate-100 data-[state=active]:text-slate-900 data-[state=active]:shadow-none text-slate-500 disabled:opacity-40" data-testid="toolkit-tab-actions">Actions</TabsTrigger>
              </TabsList>

              <div className="flex-1 overflow-y-auto px-2 py-2">
                {/* ─── SESSION TAB ─── Reset/Rehearse/Record — the "start a demo session" trio */}
                <TabsContent value="session" className="mt-0 flex flex-col gap-1.5">
            {/* 1) Rehearse — pristine tenant + new tab, no recorder */}
            <button
              onClick={() => { _trackAction('rehearse', 'click'); rehearseDemo(); }}
              disabled={busy === 'rehearse' || recording}
              className="flex items-start gap-2.5 rounded-lg px-3 py-2 text-left text-xs font-semibold text-emerald-700 transition-colors hover:bg-emerald-50 disabled:opacity-60"
              data-testid="toolkit-rehearse-demo-btn"
              title="Reset Acme + mint a fresh magic-link + open in a new tab — one click to a pristine demo session (⌘⇧R / Ctrl+Shift+R from anywhere)"
            >
              {busy === 'rehearse' ? (
                <Loader2 className="mt-0.5 h-4 w-4 shrink-0 animate-spin" />
              ) : (
                <Clapperboard className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
              )}
              <span className="flex flex-col">
                <span>Rehearse <span className="text-[10px] font-normal text-slate-400">⌘⇧R</span></span>
                <span className="text-[10px] font-normal text-slate-500">Reset + new tab (dry-run)</span>
              </span>
            </button>

            {/* 2) Record session — full-blown narration capture */}
            <button
              onClick={recording ? stopRecordingSession : startRecordingSession}
              disabled={busy === 'record-start'}
              className={`flex items-start gap-2.5 rounded-lg px-3 py-2 text-left text-xs font-semibold transition-colors disabled:opacity-60 ${
                recording
                  ? 'bg-rose-50 text-rose-700 hover:bg-rose-100'
                  : 'text-rose-700 hover:bg-rose-50'
              }`}
              data-testid="toolkit-record-session-btn"
              title={recording
                ? 'Stop the current recording and download captions'
                : 'Reset Acme + copy magic link + auto-caption every click for narration'}
            >
              {busy === 'record-start' ? (
                <Loader2 className="mt-0.5 h-4 w-4 shrink-0 animate-spin" />
              ) : recording ? (
                <Square className="mt-0.5 h-4 w-4 shrink-0 fill-rose-600 text-rose-600" />
              ) : (
                <Video className="mt-0.5 h-4 w-4 shrink-0 text-rose-600" />
              )}
              <span className="flex flex-col">
                <span>{recording ? `Stop session (${formatClock(elapsed)})` : 'Record session'}</span>
                {!recording && (
                  <span className="text-[10px] font-normal text-slate-500">Reset + caption recorder</span>
                )}
              </span>
            </button>

            {/* 3) Reset only — no tab, no recorder */}
            <button
              onClick={resetAcme}
              disabled={busy === 'reset' || recording}
              className="flex items-start gap-2.5 rounded-lg px-3 py-2 text-left text-xs font-medium text-slate-700 transition-colors hover:bg-slate-50 disabled:opacity-60"
              data-testid="toolkit-reset-acme-btn"
              title="Wipe + re-seed the Acme tenant only — no magic link, no new tab. Useful when you're already on a demo tab and just need pristine data."
            >
              {busy === 'reset' ? (
                <Loader2 className="mt-0.5 h-4 w-4 shrink-0 animate-spin" />
              ) : (
                <RotateCcw className="mt-0.5 h-4 w-4 shrink-0 text-slate-500" />
              )}
              <span className="flex flex-col">
                <span>Reset only</span>
                <span className="text-[10px] font-normal text-slate-500">Wipe + re-seed (no tab)</span>
              </span>
            </button>
                </TabsContent>

                {/* ─── NARRATION TAB ─── Generate + TTS + Save-as-preset + Preset library */}
                <TabsContent value="narration" className="mt-0 flex flex-col gap-1.5">

            {/* Empty-state: no recording captured yet → point them to the Session tab. */}
            {!recording && (!lastRecording || (lastRecording.events?.length ?? 0) === 0) && (
              <div
                className="flex flex-col items-start gap-2 rounded-lg border border-dashed border-fuchsia-200 bg-fuchsia-50/50 px-3 py-3"
                data-testid="narration-empty-state"
              >
                <div className="flex items-center gap-2">
                  <Video className="h-4 w-4 text-fuchsia-500" />
                  <span className="text-xs font-semibold text-fuchsia-800">No recording yet</span>
                </div>
                <p className="text-[11px] leading-snug text-slate-600">
                  Capture a demo session first — every click becomes a caption, then GPT-5.2 drafts a narration script from the event log.
                </p>
                <button
                  onClick={() => setActiveTab('session')}
                  className="flex items-center gap-1.5 rounded-md bg-fuchsia-600 px-2.5 py-1.5 text-[11px] font-semibold text-white transition-colors hover:bg-fuchsia-700"
                  data-testid="narration-empty-state-goto-session"
                >
                  <Video className="h-3 w-3" />
                  <span>Record a session →</span>
                </button>
              </div>
            )}

            {/* Recording-in-progress state: generate script is unavailable until you stop. */}
            {recording && (
              <div
                className="flex items-center gap-2 rounded-lg border border-rose-200 bg-rose-50/60 px-3 py-2 text-[11px] text-rose-800"
                data-testid="narration-recording-lock"
              >
                <Circle className="h-2.5 w-2.5 shrink-0 animate-pulse fill-rose-600 text-rose-600" />
                <span>Recording in progress — stop it first to generate a script.</span>
              </div>
            )}

            {/* Generate Narration — appears only after a recording has stopped */}
            {!recording && lastRecording && lastRecording.events?.length > 0 && (
              <button
                onClick={generateNarration}
                disabled={busy === 'narrate'}
                className="flex items-center gap-2.5 rounded-lg px-3 py-2 text-xs font-semibold text-fuchsia-700 transition-colors hover:bg-fuchsia-50 disabled:opacity-60"
                data-testid="toolkit-generate-narration-btn"
                title="Send the last recording to GPT-5.2 and download a voice-over script (.md)"
              >
                {busy === 'narrate' ? (
                  <Loader2 className="h-4 w-4 animate-spin text-fuchsia-500" />
                ) : (
                  <Sparkles className="h-4 w-4 text-fuchsia-500" />
                )}
                <span>Generate demo script ({lastRecording.events.length} ev)</span>
              </button>
            )}

            {/* Play script aloud — appears after narration script exists */}
            {!recording && lastScript && (
              <div className="flex flex-col gap-1 rounded-lg bg-fuchsia-50/60 px-2 py-1.5">
                <div className="flex items-center gap-2">
                  <label className="text-[10px] font-semibold uppercase tracking-wider text-fuchsia-700" htmlFor="tts-voice-select">
                    Voice
                  </label>
                  <select
                    id="tts-voice-select"
                    value={ttsVoice}
                    onChange={(e) => setTtsVoice(e.target.value)}
                    className="flex-1 rounded-md border border-fuchsia-200 bg-white px-1.5 py-0.5 text-[11px] text-slate-700 focus:border-fuchsia-500 focus:outline-none"
                    data-testid="toolkit-tts-voice-select"
                  >
                    <option value="onyx">Onyx — deep, authoritative</option>
                    <option value="sage">Sage — wise, measured</option>
                    <option value="nova">Nova — energetic, upbeat</option>
                    <option value="alloy">Alloy — neutral, balanced</option>
                    <option value="echo">Echo — smooth, calm</option>
                    <option value="fable">Fable — expressive</option>
                    <option value="shimmer">Shimmer — bright</option>
                    <option value="ash">Ash — clear, articulate</option>
                    <option value="coral">Coral — warm, friendly</option>
                  </select>
                </div>
                <button
                  onClick={playScriptAloud}
                  disabled={busy === 'tts'}
                  className="flex items-center gap-2.5 rounded-lg px-2 py-1.5 text-xs font-semibold text-fuchsia-800 transition-colors hover:bg-fuchsia-100 disabled:opacity-60"
                  data-testid="toolkit-play-aloud-btn"
                  title="Turn the generated script into a voice-over MP3 (OpenAI TTS)"
                >
                  {busy === 'tts' ? (
                    <Loader2 className="h-4 w-4 animate-spin text-fuchsia-600" />
                  ) : (
                    <Volume2 className="h-4 w-4 text-fuchsia-600" />
                  )}
                  <span>{lastAudioUrl ? 'Regenerate voice-over' : 'Play script aloud (TTS)'}</span>
                </button>

                {/* Save-as-preset — appears only when we have both script + audio */}
                {lastAudioUrl && (
                  <>
                    {!saveFormOpen ? (
                      <button
                        onClick={() => setSaveFormOpen(true)}
                        className="flex items-center gap-2.5 rounded-md px-2 py-1.5 text-xs font-semibold text-fuchsia-800 transition-colors hover:bg-fuchsia-100"
                        data-testid="toolkit-save-preset-open-btn"
                        title="Save this {recording + script + audio} bundle as a reusable sales preset"
                      >
                        <Save className="h-4 w-4 text-fuchsia-600" />
                        <span>Save as preset…</span>
                      </button>
                    ) : (
                      <div className="flex flex-col gap-1 rounded-md bg-white p-1.5 border border-fuchsia-200">
                        <input
                          autoFocus
                          type="text"
                          placeholder="Scenario name (e.g. CHRO pitch — turnover)"
                          value={saveForm.scenario}
                          onChange={(e) => setSaveForm((f) => ({ ...f, scenario: e.target.value }))}
                          className="rounded border border-slate-200 px-1.5 py-1 text-[11px] focus:border-fuchsia-400 focus:outline-none"
                          data-testid="toolkit-preset-name-input"
                        />
                        <input
                          type="text"
                          placeholder="Description (optional)"
                          value={saveForm.description}
                          onChange={(e) => setSaveForm((f) => ({ ...f, description: e.target.value }))}
                          className="rounded border border-slate-200 px-1.5 py-1 text-[11px] focus:border-fuchsia-400 focus:outline-none"
                          data-testid="toolkit-preset-desc-input"
                        />
                        <input
                          type="text"
                          placeholder="Tags, comma-separated (CHRO, turnover)"
                          value={saveForm.tags}
                          onChange={(e) => setSaveForm((f) => ({ ...f, tags: e.target.value }))}
                          className="rounded border border-slate-200 px-1.5 py-1 text-[11px] focus:border-fuchsia-400 focus:outline-none"
                          data-testid="toolkit-preset-tags-input"
                        />
                        <div className="flex gap-1 mt-0.5">
                          <button
                            onClick={savePreset}
                            disabled={busy === 'save-preset' || !saveForm.scenario.trim()}
                            className="flex-1 flex items-center justify-center gap-1 rounded-md bg-fuchsia-600 px-2 py-1 text-[11px] font-semibold text-white transition-colors hover:bg-fuchsia-700 disabled:opacity-60"
                            data-testid="toolkit-preset-save-btn"
                          >
                            {busy === 'save-preset' ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                            Save preset
                          </button>
                          <button
                            onClick={() => { setSaveFormOpen(false); setSaveForm({ scenario: '', description: '', tags: '' }); }}
                            className="rounded-md px-2 py-1 text-[11px] font-medium text-slate-600 transition-colors hover:bg-slate-100"
                            data-testid="toolkit-preset-cancel-btn"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
                </TabsContent>

                {/* ─── SEED TAB ─── Synthetic-data controls for live demo */}
                <TabsContent value="seed" className="mt-0 flex flex-col gap-1.5">
                  <DemoSeedTab
                    busy={busy}
                    triggerAnomaly={triggerAnomaly}
                    clearLiveAnomalies={clearLiveAnomalies}
                  />
                </TabsContent>

                {/* ─── CAPTURE TAB ─── Screenshot / JSON / shareable-link tools */}
                <TabsContent value="capture" className="mt-0 flex flex-col gap-1.5">
                  <DemoCaptureTab
                    busy={busy}
                    capturePng={capturePng}
                    copyJson={copyJson}
                    copyMagicLink={copyMagicLink}
                  />
                </TabsContent>

                {/* ─── ACTIONS TAB ─── Navigate / digest / presets / Director */}
                <TabsContent value="actions" className="mt-0 flex flex-col gap-1.5">
            <DemoWarmupButton onTrack={_trackAction} />
            <div className="my-1 border-t border-slate-100" />

            <button
              onClick={() => { _trackAction('open-anomalies'); navigate('/anomaly-dashboard'); }}
              className="flex items-center gap-2.5 rounded-lg px-3 py-2 text-xs font-medium text-slate-700 transition-colors hover:bg-rose-50 hover:text-rose-700"
              data-testid="toolkit-open-anomalies-btn"
            >
              <Activity className="h-4 w-4 text-rose-500" />
              <span>Open Anomaly Dashboard</span>
            </button>
            <button
              onClick={() => { _trackAction('open-data-health'); navigate('/admin/data-health'); }}
              className="flex items-center gap-2.5 rounded-lg px-3 py-2 text-xs font-medium text-slate-700 transition-colors hover:bg-emerald-50 hover:text-emerald-700"
              data-testid="toolkit-open-data-health-btn"
            >
              <Activity className="h-4 w-4 text-emerald-500" />
              <span>Open Data Health</span>
            </button>

            <button
              onClick={sendDigestNow}
              disabled={busy === 'digest'}
              className="flex items-center gap-2.5 rounded-lg px-3 py-2 text-xs font-medium text-indigo-700 transition-colors hover:bg-indigo-50 disabled:opacity-60"
              data-testid="toolkit-send-digest-btn"
              title="Email all super-admins a rollup of last 7 days of preset engagement"
            >
              {busy === 'digest' ? (
                <Loader2 className="h-4 w-4 animate-spin text-indigo-500" />
              ) : (
                <Mail className="h-4 w-4 text-indigo-500" />
              )}
              <span>Send digest now</span>
            </button>
            <button
              onClick={togglePresets}
              className="flex items-center gap-2.5 rounded-lg px-3 py-2 text-xs font-medium text-fuchsia-700 transition-colors hover:bg-fuchsia-50"
              data-testid="toolkit-presets-toggle-btn"
              title="Browse saved {recording + script + audio} sales presets"
            >
              <BookMarked className="h-4 w-4 text-fuchsia-500" />
              <span>{presetsOpen ? 'Hide' : 'Browse'} presets {presets.length ? `(${presets.length})` : ''}</span>
              {presetsOpen ? <ChevronUp className="ml-auto h-3.5 w-3.5 text-slate-400" /> : <ChevronDown className="ml-auto h-3.5 w-3.5 text-slate-400" />}
            </button>
            {presetsOpen && (
              <DemoPresetsList
                presets={presets}
                presetsLoading={presetsLoading}
                busy={busy}
                playPreset={playPreset}
                copyPublicLink={copyPublicLink}
                deletePreset={deletePreset}
              />
            )}

            <div className="my-1 border-t border-slate-100" />
            <button
              onClick={() => { _trackAction('open-director'); setExpanded(false); setDirectorOpen(true); }}
              className="flex items-center gap-2.5 rounded-lg px-3 py-2 text-xs font-semibold text-fuchsia-700 transition-colors hover:bg-fuchsia-50"
              data-testid="toolkit-open-director-btn"
              title="Dial Acme's narrative arc — industry, dip severity, recovery, primary constraint, final band, seed"
            >
              <Wand2 className="h-4 w-4 text-fuchsia-500" />
              <span>Demo Story Director</span>
            </button>
                </TabsContent>
              </div>
            </Tabs>
          </div>
        )}

        <button
          onClick={() => setExpanded((e) => !e)}
          className={`hidden flex items-center gap-2 rounded-full px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-white shadow-lg transition-transform hover:scale-105 active:scale-95 ${
            recording
              ? 'bg-gradient-to-r from-rose-600 to-rose-500 animate-pulse'
              : 'bg-gradient-to-r from-indigo-600 to-violet-600'
          }`}
          data-testid="demo-toolkit-toggle"
          aria-hidden="true"
          tabIndex={-1}
        >
          {recording ? <Circle className="h-4 w-4 fill-white" /> : <FlaskConical className="h-4 w-4" />}
          <span>{recording ? `REC ${formatClock(elapsed)}` : 'Demo Toolkit'}</span>
          {expanded ? <ChevronDown className="h-3.5 w-3.5 opacity-80" /> : <ChevronUp className="h-3.5 w-3.5 opacity-80" />}
        </button>
      </div>

      {/* Demo Story Director modal — controlled outside the dock so it
          stays mounted independent of expanded/collapsed dock state. */}
      <DemoDirector
        open={directorOpen}
        onOpenChange={(o) => {
          setDirectorOpen(o);
          if (!o) clearBatchReengageIntent();
        }}
        batchReengageIds={pendingBatchReengageIds}
        onBatchReengageConsumed={clearBatchReengageIntent}
      />
    </>
  );
};

export default DemoToolkit;
